"use client";

import { useEffect, useState, type ReactNode } from "react";
import { useWorkspace } from "@/app/components/workspace/WorkspaceContext";
import {
  clearIndustryPresetIntent,
  currentIndustryPresetIntentHash,
  industryPresetSlugFromHash,
} from "@/app/lib/industryPresetIntent";
import { getIndustrySetupDriver } from "../onboarding/registry";
import { getPresetCodeForPublicIndustrySlug } from "../presetIndustryPresentation";
import { IndustryOnboardingWizard } from "./IndustryOnboardingWizard";

export function IndustryOnboardingBoundary({ children }: { children: ReactNode }) {
  const { user } = useWorkspace();
  const [industrySlug, setIndustrySlug] = useState<string | null>(null);
  const intentKey = `industry-setup-intent:${user.uuid}`;
  const presetCode = industrySlug ? getPresetCodeForPublicIndustrySlug(industrySlug) : undefined;
  const driver = presetCode ? getIndustrySetupDriver(presetCode) : undefined;

  useEffect(() => {
    function readIntent(restore: boolean) {
      let slug = industryPresetSlugFromHash(currentIndustryPresetIntentHash());
      try {
        if (restore && !window.location.hash) slug ??= window.sessionStorage.getItem(intentKey);
        const code = slug ? getPresetCodeForPublicIndustrySlug(slug) : undefined;
        if (code && getIndustrySetupDriver(code)) {
          window.sessionStorage.setItem(intentKey, slug!);
        } else {
          window.sessionStorage.removeItem(intentKey);
          slug = null;
        }
      } catch {
        // The URL still carries the current intent when browser storage is blocked.
      }
      setIndustrySlug(slug);
    }

    readIntent(true);
    const onHashChange = () => readIntent(false);
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [intentKey]);

  function close() {
    try {
      window.sessionStorage.removeItem(intentKey);
    } catch {
      // Clearing the URL is enough to dismiss the current flow.
    }
    setIndustrySlug(null);
    clearIndustryPresetIntent();
  }

  return driver ? (
    <IndustryOnboardingWizard
      key={`${user.uuid}:${driver.presetCode}`}
      driver={driver}
      onClose={close}
    />
  ) : children;
}
